import React, {
  useEffect, useState, useCallback, useMemo,
} from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Box, Stack, Typography } from '@mui/material';
import { BackButton } from '@marageti/z4-lib';
import VersionComparisonView from './version-comparison-view';
import { useEntityVersions } from '../use-entity-versions';
import { useVersionDiff } from '../use-version-diff';
import { EntityType, AnyEntityVersion, VersionsFilters } from '../../types';

const VersionComparisonContainer: React.FC = () => {
  const { entityType, id } = useParams<{ entityType: string; id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const updateId = searchParams.get('updateId');
  const indexParam = searchParams.get('index');
  const allParam = searchParams.get('all');

  // Build the back URL preserving the original versions search params
  const versionsBackPath = entityType && id
    ? `/settings/audit/versions?entityType=${entityType}&id=${id}${allParam === 'true' ? '&all=true' : ''}`
    : '/settings/audit/versions';

  const filters: VersionsFilters = useMemo(() => ({
    entityType: (entityType as EntityType) || undefined,
    id: id || undefined,
    all: allParam === 'true',
  }), [entityType, id, allParam]);

  const {
    rawVersions,
    isLoading,
    error,
  } = useEntityVersions(filters);

  const [currentVersion, setCurrentVersion] = useState<AnyEntityVersion | null>(null);
  const [previousVersion, setPreviousVersion] = useState<AnyEntityVersion | null>(null);
  const [currentIndex, setCurrentIndex] = useState<number>(0);

  // Set current and previous versions based on index or updateId
  useEffect(() => {
    if (rawVersions.length === 0) return;

    let index = 0;

    if (indexParam) {
      const parsed = parseInt(indexParam, 10);
      if (!Number.isNaN(parsed)) {
        index = parsed;
      }
    } else if (updateId) {
      // Find version by updateId
      const foundIndex = rawVersions.findIndex((v) => v.updateId === updateId);
      if (foundIndex !== -1) {
        index = foundIndex;
      }
    }

    // Clamp index to valid range
    index = Math.max(0, Math.min(index, rawVersions.length - 1));

    setCurrentIndex(index);
    setCurrentVersion(rawVersions[index]);

    // Previous version is the previous one in the array (older), since array is sorted Oldest -> Newest
    // So if current index is i, previous version is at i - 1
    if (index > 0) {
      setPreviousVersion(rawVersions[index - 1]);
    } else {
      setPreviousVersion(null);
    }
  }, [rawVersions, updateId, indexParam]);

  const [showUnchanged, setShowUnchanged] = useState<boolean>(false);

  // Compute diffs between current and previous versions
  const diffs = useVersionDiff(currentVersion, previousVersion, showUnchanged);

  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < rawVersions.length - 1;

  const handlePrevious = useCallback(() => {
    if (hasPrevious) {
      const newIndex = currentIndex - 1;
      const prevVersion = rawVersions[newIndex];
      // Replace history entry so Prev/Next don't pile up in browser history
      setSearchParams({
        updateId: prevVersion.updateId,
        all: String(allParam === 'true'),
      }, { replace: true });
    }
  }, [currentIndex, hasPrevious, rawVersions, setSearchParams, allParam]);

  const handleNext = useCallback(() => {
    if (hasNext) {
      const newIndex = currentIndex + 1;
      const nextVersion = rawVersions[newIndex];
      // Replace history entry so Prev/Next don't pile up in browser history
      setSearchParams({
        updateId: nextVersion.updateId,
        all: String(allParam === 'true'),
      }, { replace: true });
    }
  }, [currentIndex, hasNext, rawVersions, setSearchParams, allParam]);

  return (
    <Box sx={{ mb: 4 }}>
      <Stack alignItems="center" justifyContent="flex-start" flexDirection="row" gap={1} sx={{ mb: 2 }}>
        <BackButton defaultPath={versionsBackPath} />
        <Typography variant="h5">
          Entity Versions Comparison
        </Typography>
      </Stack>
      <VersionComparisonView
        currentVersion={currentVersion}
        previousVersion={previousVersion}
        entityType={entityType as EntityType}
        entityId={id || ''}
        currentIndex={currentIndex}
        totalVersions={rawVersions.length}
        diffs={diffs}
        isLoading={isLoading}
        error={error}
        onPrevious={handlePrevious}
        onNext={handleNext}
        hasPrevious={hasPrevious}
        hasNext={hasNext}
        showUnchanged={showUnchanged}
        onToggleUnchanged={() => setShowUnchanged((prev) => !prev)}
      />
    </Box>
  );
};

export default VersionComparisonContainer;
